import React, { useState, useEffect, useCallback } from 'react'
import { Search, Plus, Edit, Trash2, Eye, RefreshCw, ChevronDown, CheckCircle, XCircle, X, Filter, Clock, Calendar, AlertCircle } from 'lucide-react'
import { employeeShiftService } from '../../../services/employeeShiftService'
import branchService from '../../../services/branchService'
import { jobService } from '../../../services/jobService'
import InlineLoader from '../../../components/ui/InlineLoader'
import AssignShiftModal from '../../../components/modals/AssignShiftModal'
import EditEmployeeShiftModal from '../../../components/modals/EditEmployeeShiftModal'
import ViewEmployeeShiftModal from '../../../components/modals/ViewEmployeeShiftModal'
import DeleteEmployeeShiftModal from '../../../components/modals/DeleteEmployeeShiftModal'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-2 ${
    type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
    <span>{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2">
      <X className="h-4 w-4" />
    </button>
  </div>
)

const EmployeeShifts = () => {
  const [employeeShifts, setEmployeeShifts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [branchFilter, setBranchFilter] = useState('all')
  const [shiftFilter, setShiftFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [branches, setBranches] = useState([])
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedEmployeeShift, setSelectedEmployeeShift] = useState(null)
  const [toastMessage, setToastMessage] = useState(null)

  
  // Lookup maps for converting IDs to names
  const [jobsMap, setJobsMap] = useState({})
  const [branchesMap, setBranchesMap] = useState({})

  // Helper function to safely get property value
  const safeGet = (obj, path, defaultValue = '') => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  // Helper function to get branch name - handles both populated and non-populated data
  const getBranchName = (employee) => {
    if (!employee) return 'N/A'
    if (typeof employee.branch === 'object' && employee.branch?.name) {
      return employee.branch.name
    }
    // Use lookup map to convert ID to name
    if (employee.branch && branchesMap[employee.branch]) {
      return branchesMap[employee.branch]
    }
    return employee.branch || 'N/A'
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToastMessage({ message, type })
    setTimeout(() => setToastMessage(null), 3000)
  }

  // Fetch lookup data for jobs and branches
  const fetchLookupData = useCallback(async () => {
    try {
      const [jobsResponse, branchesResponse] = await Promise.all([
        jobService.getAllJobs(),
        branchService.getAllBranches()
      ])

      // Create jobs lookup map
      if (jobsResponse?.success && jobsResponse?.data?.jobs) {
        const jobsLookup = {}
        jobsResponse.data.jobs.forEach(job => {
          jobsLookup[job._id] = job.name
        })
        setJobsMap(jobsLookup)
      }

      // Create branches lookup map
      if (branchesResponse?.success && branchesResponse?.data?.branches) {
        const branchesLookup = {}
        branchesResponse.data.branches.forEach(branch => {
          branchesLookup[branch._id] = branch.name
        })
        setBranchesMap(branchesLookup)
      }
    } catch (error) {
      console.error('Error fetching lookup data:', error)
    }
  }, [])

  // Fetch employee shifts
  const fetchEmployeeShifts = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const params = {}
      if (branchFilter !== 'all') params.branch = branchFilter
      if (shiftFilter !== 'all') params.shift = shiftFilter
      if (statusFilter !== 'all') params.status = statusFilter
      if (searchTerm) params.search = searchTerm

      const response = await employeeShiftService.getEmployeeShifts(params)
      
      if (response?.success && response?.data) {
        setEmployeeShifts(response.data.employeeShifts || [])
      } else {
        setError('Failed to fetch employee shifts')
      }
    } catch (err) {
      console.error('Error fetching employee shifts:', err)
      setError('Failed to fetch employee shifts')
    } finally {
      setLoading(false)
    }
  }, [branchFilter, shiftFilter, statusFilter, searchTerm])

  // Fetch branches for filter
  const fetchBranches = async () => {
    try {
      const response = await branchService.getAllBranches()
      if (response?.success && response?.data) {
        setBranches(response.data.branches || [])
      }
    } catch (err) {
      console.error('Error fetching branches:', err)
    }
  }

  // Initial data fetch
  useEffect(() => {
    fetchEmployeeShifts()
    fetchBranches()
    fetchLookupData()
  }, [fetchEmployeeShifts, fetchLookupData])

  // Refetch when filters change
  useEffect(() => {
    const delayedFetch = setTimeout(() => {
      fetchEmployeeShifts()
    }, 300)

    return () => clearTimeout(delayedFetch)
  }, [fetchEmployeeShifts])

  // Handle assign shift success
  const handleAssignSuccess = () => {
    fetchEmployeeShifts()
    setShowAssignModal(false)
    showToast('Employee shift assigned successfully!')
  }

  // Handle edit success
  const handleEditSuccess = () => {
    fetchEmployeeShifts()
    setShowEditModal(false)
    showToast('Employee shift updated successfully!')
  }

  // Handle delete success
  const handleDeleteSuccess = () => {
    fetchEmployeeShifts()
    setShowDeleteModal(false)
    showToast('Employee shift deleted successfully!')
  }

  // Get status badge color
  const getStatusBadge = (status) => {
    const colors = {
      open: 'bg-green-100 text-green-800',
      closed: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      inactive: 'bg-gray-100 text-gray-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString()
  }

  // Filter employee shifts based on search
  const filteredEmployeeShifts = employeeShifts.filter(shift => {
    const employeeName = safeGet(shift, 'employee.fullName', '').toLowerCase()
    const shiftName = safeGet(shift, 'shift.shiftName', '').toLowerCase()
    const branchName = getBranchName(shift.employee).toLowerCase()
    
    return employeeName.includes(searchTerm.toLowerCase()) ||
           shiftName.includes(searchTerm.toLowerCase()) ||
           branchName.includes(searchTerm.toLowerCase())
  })

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Employee Shifts</h1>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchEmployeeShifts}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => setShowAssignModal(true)}
            className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Assign Shift</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filter Employee Shifts</h2>
          </div>
          {(searchTerm || branchFilter !== 'all' || shiftFilter !== 'all' || statusFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchTerm('')
                setBranchFilter('all')
                setShiftFilter('all')
                setStatusFilter('all')
              }}
              className="px-4 py-2 text-orange-600 border border-orange-600 rounded-lg hover:bg-orange-50 transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by employee, shift, or branch..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-3 w-full border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Branch Filter */}
          <div className="relative">
            <select
              value={branchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none pr-10 bg-white"
            >
              <option value="all">All Branches</option>
              {branches.map(branch => (
                <option key={branch._id} value={branch._id}>
                  {branch.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none pr-10 bg-white"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="open">Open</option>
              <option value="closed">Close</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Results count */}
        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredEmployeeShifts.length} of {employeeShifts.length} employee shifts
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <InlineLoader />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Shift
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Branch
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Start Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    End Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEmployeeShifts.length > 0 ? (
                  filteredEmployeeShifts.map((employeeShift) => (
                    <tr key={employeeShift._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {safeGet(employeeShift, 'employee.fullName', 'N/A')}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {safeGet(employeeShift, 'employee.employeeId') || 
                                   safeGet(employeeShift, 'employee._id')?.slice(-8).toUpperCase() || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {safeGet(employeeShift, 'shift.shiftName', 'N/A')}
                            </div>
                            <div className="text-sm text-gray-500">
                              {safeGet(employeeShift, 'shift.startTime', 'N/A')} - {safeGet(employeeShift, 'shift.endTime', 'N/A')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getBranchName(employeeShift.employee)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                          {formatDate(employeeShift.startDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                          {formatDate(employeeShift.endDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(employeeShift.status)}`}>
                          {employeeShift.status || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => {
                              setSelectedEmployeeShift(employeeShift)
                              setShowViewModal(true)
                            }}
                            className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedEmployeeShift(employeeShift)
                              setShowEditModal(true)
                            }}
                            className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedEmployeeShift(employeeShift)
                              setShowDeleteModal(true)
                            }}
                            className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      No employee shifts found. Click "Assign Shift" to add one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {showAssignModal && (
        <AssignShiftModal
          onClose={() => setShowAssignModal(false)}
          onSuccess={handleAssignSuccess}
          jobsMap={jobsMap}
          branchesMap={branchesMap}
        />
      )}

      {showEditModal && selectedEmployeeShift && (
        <EditEmployeeShiftModal
          employeeShift={selectedEmployeeShift}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleEditSuccess}
        />
      )}

      {showViewModal && selectedEmployeeShift && (
        <ViewEmployeeShiftModal
          employeeShift={selectedEmployeeShift}
          onClose={() => setShowViewModal(false)}
          jobsMap={jobsMap}
          branchesMap={branchesMap}
        />
      )}

      {showDeleteModal && selectedEmployeeShift && (
        <DeleteEmployeeShiftModal
          employeeShift={selectedEmployeeShift}
          onClose={() => setShowDeleteModal(false)}
          onSuccess={handleDeleteSuccess}
        />
      )}

      {/* Toast */}
      {toastMessage && (
        <Toast
          message={toastMessage.message}
          type={toastMessage.type}
          onClose={() => setToastMessage(null)}
        />
      )}
    </div>
  )
}

export default EmployeeShifts
